import { MAX_DAYS_DEFAULT } from '../model/Vehicle';

import {
  addDays,
  addMinutes,
  addWeeks,
  compareDatelikeAsc,
  type Datelike,
  getEndOfDay,
  getStartOfDay,
  isAfter,
  isBefore,
  isDaysBefore,
  isMinutesAfter,
  isMinutesBefore,
  isSameDay,
  isSameDayOrAfter,
  isSameOrAfter,
  isSameOrBefore,
  MIN_MINUTES_IN_RANGE,
  preserveTime,
  roundTime,
  setWeekday,
  toDate,
} from './DateTime';

export enum DateTimeRangeSide {
  NONE = 0,
  LOCATION = 1,
  DATE_FROM = 2,
  TIME_FROM = 3,
  DATE_TO = 4,
  TIME_TO = 5,
}

export function getNextDateTimeRangeSideInFlow(
  current: DateTimeRangeSide,
  touched: Partial<Record<DateTimeRangeSide, boolean>>,
): DateTimeRangeSide {
  if ([DateTimeRangeSide.DATE_FROM, DateTimeRangeSide.TIME_FROM, DateTimeRangeSide.DATE_TO].includes(current)) {
    const next = current + 1;
    return next in touched && touched[next] ? DateTimeRangeSide.NONE : next;
  }
  return DateTimeRangeSide.NONE;
}

export interface DateRangeOptions {
  min_days: number;
  max_days: number;
  disabledDates: Datelike[];
  disabledWeekdays: number[];
  enabledDates?: Datelike[];
}

export function alignDateTime(value: Datelike, [min, max]: [Datelike, Datelike] = [null, null]): Datelike {
  let result = value;
  if (min && isMinutesBefore(value, min)) {
    result = min;
  }
  if (max && isMinutesAfter(value, max)) {
    result = max;
  }
  return result;
}

export function alignDateTimeToMinRange(value: Datelike): Datelike {
  return addMinutes(value, MIN_MINUTES_IN_RANGE);
}

function getDisabledDatesFrom(from: Datelike, options: Partial<DateRangeOptions>): Datelike[] {
  const disabledDates = [];
  if (options.disabledWeekdays && options.disabledWeekdays.length) {
    disabledDates.push(
      ...options.disabledWeekdays.map(wd => {
        let result = setWeekday(from, wd);
        if (isDaysBefore(result, from)) {
          result = addWeeks(result, 1);
        }
        return result;
      }),
    );
  }
  if (options.disabledDates && options.disabledDates.length) {
    disabledDates.push(...options.disabledDates.filter(d => isSameDayOrAfter(d, from)));
  }

  const result = disabledDates.map(toDate).sort(compareDatelikeAsc);
  if (options.enabledDates?.length) {
    return result.filter(it => !options.enabledDates.some(ed => isSameDay(it, ed)));
  }
  return result;
}

export function getFirstAvailableDateFrom(from: Datelike, options: Partial<DateRangeOptions> = {}): Datelike {
  let result: Datelike = from;
  const disabledDates = getDisabledDatesFrom(from, options);

  if (disabledDates.length) {
    for (const date of disabledDates) {
      if (isSameDay(date, result)) {
        result = preserveTime(addDays(date, 1), result);
      }
    }
  }

  if (typeof options.min_days === 'number' && options.min_days - 1 > 0) {
    const min = addDays(from, options.min_days - 1);
    if (isAfter(min, result)) {
      result = min;
    }
  }

  return roundTime(result);
}

export function getLastAvailableDateFrom(from: Datelike, options: Partial<DateRangeOptions> = {}): Datelike {
  let result: Datelike;
  const disabledDates = getDisabledDatesFrom(from, options);

  if (disabledDates.length) {
    const firstDisabledDate = disabledDates.find(dd => isMinutesAfter(dd, from));
    if (firstDisabledDate) {
      result = roundTime(firstDisabledDate);
    }
  }

  if (typeof options.max_days === 'number' && options.max_days !== MAX_DAYS_DEFAULT) {
    const max = addDays(roundTime(from), options.max_days > 1 ? options.max_days - 1 : options.max_days);
    if (!result || isMinutesAfter(result, max)) {
      result = max;
    }
  }

  return result;
}

export function getDateRangeBoundsFor(ctx: Datelike, options: Partial<DateRangeOptions> = {}): [Datelike, Datelike] {
  let min = getFirstAvailableDateFrom(ctx, options);
  if (isSameOrBefore(min, ctx)) {
    min = alignDateTimeToMinRange(min);
  }
  const max = getLastAvailableDateFrom(min, options);
  return [min, max];
}

interface DateRange {
  from: Datelike;
  to: Datelike;
  bounds_from: [Datelike, Datelike];
  bounds_to: [Datelike, Datelike];
}

export function computeDateRange(
  from: Datelike = new Date(),
  to: Datelike = new Date(),
  options: Partial<DateRangeOptions> = {},
): DateRange {
  from = roundTime(from ?? new Date());
  to = roundTime(to ?? new Date());

  const from_min = getFirstAvailableDateFrom(new Date(), {
    ...options,
    min_days: undefined,
  });
  from = alignDateTime(from, [from_min, null]);

  const [to_min, to_max] = getDateRangeBoundsFor(from, options);
  to = alignDateTime(to, [to_min, to_max]);

  return {
    from,
    to,
    bounds_from: [from_min, null],
    bounds_to: [to_min, to_max],
  };
}

export function isDaysInDateRangeBounds(it: Datelike, min: Datelike, max: Datelike): boolean {
  if (!min && !max) return true;
  const start = getEndOfDay(it);
  const end = getStartOfDay(it);
  if (!max) {
    return isSameOrAfter(start, min);
  }
  if (!min) {
    return isBefore(end, max);
  }
  return isSameOrAfter(start, min) && isBefore(end, max);
}
